import { useDraggable } from '@dnd-kit/core';
import { FormControl, Stack, TextField, Typography, useTheme } from '@mui/material';
import { Label, LabelGroup } from '@provision/entity-types';
import { useCallback, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { DynamicIcon, EllipsesMenu, Tooltip, WithTooltip } from 'src/components';
import { NON_LABEL_GROUP_ID } from 'src/constants/label';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { buildLabelsFilters, useLabel } from 'src/graphql';
import { ConditionInput } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { pickPastelColor } from 'src/utils/strings';

export interface LabelFormValues {
  editLabel?: string;
}

export const LabelForm = ({
  label,
  group,
  onSubmit,
  labelsFilters,
  partnerId,
  includeOnlyOutsideGroups,
}: {
  label?: Label;
  group?: LabelGroup;
  onSubmit: () => void;
  labelsFilters: ConditionInput[];
  partnerId?: number | null;
  includeOnlyOutsideGroups?: boolean;
}) => {
  const formMethods = useForm<LabelFormValues>({
    defaultValues: {
      editLabel: label?.displayName ?? '',
    },
  });
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const resolvedPartnerId = partnerId ?? selectedPartnerId;
  const { showSuccessAlert } = useNotifications();

  const effectiveFilters = useMemo(
    () =>
      buildLabelsFilters({
        partnerId: resolvedPartnerId,
        includeOnlyOutsideGroups,
        extraFilters: labelsFilters,
      }),
    [includeOnlyOutsideGroups, labelsFilters, resolvedPartnerId],
  );

  const { createLabel, updateLabel } = useLabel({
    filters: effectiveFilters,
  });

  const { control, handleSubmit } = formMethods;

  const onSubmitLabelForm = useCallback(
    async (data: LabelFormValues) => {
      if (!data.editLabel) {
        return;
      }

      onSubmit();

      if (label) {
        await updateLabel(
          {
            ...label,
            displayName: data.editLabel || label.displayName,
          },
          label.labelGroupId ?? null,
        );
      } else {
        const createdLabel = await createLabel(
          {
            labelGroupId: group?.labelGroupId ?? null,
            displayName: data.editLabel,
          },
          resolvedPartnerId ?? undefined,
        );

        if (!createdLabel?.[0]) {
          return;
        }
      }

      showSuccessAlert(label ? 'Label was updated' : 'Label was created');
    },
    [
      onSubmit,
      label,
      showSuccessAlert,
      updateLabel,
      createLabel,
      group?.labelGroupId,
      resolvedPartnerId,
    ],
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmitLabelForm)}>
        <FormControl
          sx={{
            marginTop: 1,
          }}
        >
          <Controller
            name="editLabel"
            control={control}
            render={({ field }) => (
              <TextField
                autoFocus
                variant="outlined"
                size="small"
                label="Label name"
                inputProps={{
                  onBlur: ({ target }) => {
                    const value = (target as HTMLInputElement).value;

                    if (!value || value === label?.displayName) {
                      onSubmit();
                    }
                  },
                }}
                {...field}
              />
            )}
          />
        </FormControl>
      </form>
    </FormProvider>
  );
};

export const DraggableLabel = ({
  label,
  group,
  labelsFilters,
}: {
  label: Label;
  group?: LabelGroup;
  partnerId?: number | null;
  includeOnlyOutsideGroups?: boolean;
  labelsFilters: ConditionInput[];
}) => {
  const theme = useTheme();

  const backgroundColor = useMemo(() => {
    return pickPastelColor(group?.labelGroupId ?? NON_LABEL_GROUP_ID, theme.palette.mode);
  }, [group?.labelGroupId, theme.palette.mode]);

  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const hasAccessToMenu =
    !isMultiTenancyMode ||
    selectedPartnerId === PLATFORM_PARTNER_ID ||
    label.partnerId !== PLATFORM_PARTNER_ID;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: label.labelId,
    data: {
      ...label,
      labelGroupId: group?.labelGroupId ?? label.labelGroupId,
    },
    disabled: !hasAccessToMenu,
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  let cursorStyle: 'auto' | 'grab' | 'grabbing' = 'auto';

  if (isDragging) {
    cursorStyle = 'grabbing';
  } else if (hasAccessToMenu) {
    cursorStyle = 'grab';
  }

  const { updateLabel, deleteLabel } = useLabel({
    filters: labelsFilters,
  });

  const { showSuccessAlert } = useNotifications();

  const menuOptions = useMemo(
    () => [
      {
        label: 'Edit',
        icon: 'pencil',
        iconLibrary: 'lucide' as const,
        onClick: () => {
          setIsMenuOpen(false);
          setIsEditing(true);
        },
      },
      {
        label: 'Delete',
        icon: 'trash-2',
        iconLibrary: 'lucide' as const,
        onClick: async () => {
          setIsMenuOpen(false);

          const result = await deleteLabel({
            ...label,
            partnerId: label.partnerId ?? selectedPartnerId,
            labelGroupId: group?.labelGroupId ?? label.labelGroupId,
          });
          if (!result?.errors) {
            showSuccessAlert('Label was deleted');
          }
        },
      },
      {
        label: label.isPublic ? 'Hide' : 'Unhide',
        icon: label.isPublic ? 'eye-off' : 'eye',
        iconLibrary: 'lucide' as const,
        onClick: async () => {
          await updateLabel(
            {
              ...label,
              isPublic: !label.isPublic,
            },
            label.labelGroupId ?? null,
          );
          showSuccessAlert('Label was updated');
        },
      },
    ],
    [
      deleteLabel,
      group?.labelGroupId,
      label,
      selectedPartnerId,
      showSuccessAlert,
      updateLabel,
    ],
  );

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onMouseEnter={() => {
        setIsMenuOpen(true);
      }}
      onMouseLeave={() => {
        setIsMenuOpen(false);
      }}
      sx={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : null,
        opacity: isDragging ? 0.5 : 1,
        cursor: cursorStyle,
        zIndex: theme.zIndex.app.ServiceDeskConversations,
      }}
    >
      {isEditing ? (
        <LabelForm
          onSubmit={() => setIsEditing(false)}
          labelsFilters={labelsFilters}
          label={label}
          group={group}
        />
      ) : null}
      {!isEditing && (
        <>
          <Tooltip
            title="Drag and drop to move to another group"
            enterDelay={500}
            hideWhenRefIsHidden
          >
            <DynamicIcon
              name="grip-vertical"
              library="lucide"
              size={24}
              sx={{
                width: '1.5rem',
                opacity: isMenuOpen && hasAccessToMenu ? 1 : 0,
                pointerEvents: !hasAccessToMenu ? 'none' : undefined,
              }}
            />
          </Tooltip>
          <WithTooltip hideTooltip={hasAccessToMenu} title="Default label">
            <Stack direction="row" alignItems="center" spacing={1}>
              <DynamicIcon
                name="tag"
                library="lucide"
                color={backgroundColor}
                sx={{ '& svg': { fill: backgroundColor } }}
              />
              <Typography variant="caption" fontSize="0.75rem">
                {label.displayName}
              </Typography>
            </Stack>
          </WithTooltip>
          {!label.isPublic && (
            <Tooltip title="Private">
              <DynamicIcon name="eye-off" library="lucide" />
            </Tooltip>
          )}
          {isMenuOpen && hasAccessToMenu ? (
            <EllipsesMenu options={menuOptions} icon="more-horizontal" iconLibrary="lucide" />
          ) : null}
        </>
      )}
    </Stack>
  );
};
